import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getActivity } from '../lib/api';
import { ActivityDay } from '../types/content';
import './ActivityHeatmap.css';

const WEEKDAY_LABELS = ['', 'Mon', '', 'Wed', '', 'Fri', ''];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Local YYYY-MM-DD, matching the keys the API now aggregates by.
 *
 * Deliberately not `toISOString().slice(0,10)`, which is UTC: east of
 * Greenwich that names the wrong day for the whole evening, so a post
 * published at 3am local was filed under the previous date and the current
 * day never appeared on the grid at all.
 */
const isoDate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;

/** A YYYY-MM-DD key back to a Date at local midnight. */
const fromIso = (key: string) => {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d);
};

/**
 * Four shades plus empty, like GitHub. Thresholds are absolute rather than
 * relative to the busiest day: a blog publishes a handful of posts a week at
 * most, so scaling to the maximum would make a single busy day wash out
 * everything else.
 */
function level(count: number): number {
  if (count <= 0) return 0;
  if (count === 1) return 1;
  if (count === 2) return 2;
  if (count <= 4) return 3;
  return 4;
}

interface Cell {
  date: string;
  count: number;
  authors: Record<string, number>;
  inFuture: boolean;
}

/**
 * Builds whole weeks, Sunday-first, so every column is a week and every row a
 * weekday — the arrangement that makes streaks legible at a glance.
 *
 * The window always ends on today and the trailing week is padded out to seven
 * days, so the grid keeps a rectangular right edge instead of a ragged one.
 */
function buildWeeks(days: ActivityDay[], windowDays: number): Cell[][] {
  const byDate = new Map(days.map((d) => [d.date, d]));

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const start = new Date(today);
  start.setDate(start.getDate() - (windowDays - 1));
  // Rewind to the Sunday on or before the start, so the first column is whole.
  start.setDate(start.getDate() - start.getDay());

  const weeks: Cell[][] = [];
  let current: Cell[] = [];

  for (const cursor = new Date(start); ; cursor.setDate(cursor.getDate() + 1)) {
    const key = isoDate(cursor);
    const found = byDate.get(key);

    current.push({
      date: key,
      count: found?.count ?? 0,
      authors: found?.authors ?? {},
      inFuture: cursor > today,
    });

    if (current.length === 7) {
      weeks.push(current);
      current = [];
      if (cursor >= today) break;
    }
  }

  return weeks;
}

interface TipContent {
  headline: string;
  authors: string;
}

function describe(cell: Cell): TipContent | null {
  if (!cell.date || cell.inFuture) return null;

  const when = fromIso(cell.date).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  if (cell.count === 0) return { headline: `No posts on ${when}`, authors: '' };

  return {
    headline: `${cell.count} post${cell.count === 1 ? '' : 's'} on ${when}`,
    authors: Object.entries(cell.authors)
      .map(([name, n]) => `${name}${n > 1 ? ` (${n})` : ''}`)
      .join(', '),
  };
}

const flatten = (tip: TipContent) =>
  tip.authors ? `${tip.headline} — ${tip.authors}` : tip.headline;

interface TipState extends TipContent {
  /** Viewport centre of the anchoring square, and its top/bottom edges. */
  cx: number;
  top: number;
  bottom: number;
}

export interface ActivityHeatmapProps {
  /** How far back to show. Defaults to a year. */
  days?: number;
  title?: string;
}

const ActivityHeatmap: React.FC<ActivityHeatmapProps> = ({
  days = 365,
  title = 'Publishing activity',
}) => {
  const [data, setData] = useState<{
    activity: ActivityDay[];
    total: number;
    busiestDay: { date: string; count: number } | null;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tip, setTip] = useState<TipState | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);

  const showTip = useCallback((target: HTMLElement, cell: Cell) => {
    const content = describe(cell);
    if (!content) return;
    const box = target.getBoundingClientRect();
    setTip({
      ...content,
      cx: box.left + box.width / 2,
      top: box.top,
      bottom: box.bottom,
    });
  }, []);

  const hideTip = useCallback(() => setTip(null), []);

  useEffect(() => {
    let cancelled = false;
    getActivity(days)
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Could not load activity.');
      });
    return () => {
      cancelled = true;
    };
  }, [days]);

  const weeks = useMemo(() => (data ? buildWeeks(data.activity, days) : []), [data, days]);

  /*
   * A month is labelled above the column containing its 1st.
   *
   * Keying off each column's own month instead — "label it when the month
   * changes" — labels the current month twice over. The window is 53 weeks, so
   * it begins and ends inside the same month: the far-left partial column got
   * the label and the real, current month at the right edge got none, leaving
   * the axis reading Aug…Jul in August.
   *
   * Anchoring on the 1st also means no two labels can ever land closer than
   * four columns, so they cannot overlap and need no spacing rule.
   */
  const monthLabels = useMemo(() => {
    const labels: Array<{ index: number; label: string }> = [];

    weeks.forEach((week, index) => {
      const firstOfMonth = week.find((c) => c.date.endsWith('-01') && !c.inFuture);
      if (!firstOfMonth) return;
      const month = fromIso(firstOfMonth.date).getMonth();
      labels.push({ index, label: MONTHS[month] });
    });

    // On the 1st of a month, last year's 1st is still inside the window and a
    // thirteenth label appears — the same month named at both ends. The older
    // one is the one to lose.
    return labels.slice(-12);
  }, [weeks]);

  /*
   * Open on the current month. The grid is a year wide and scrolls on any
   * screen narrower than about 800px, so left-aligned it opens on activity
   * from twelve months ago and the recent weeks — the interesting part — are
   * off-screen until you scroll.
   */
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || weeks.length === 0) return;
    el.scrollLeft = el.scrollWidth;
  }, [weeks]);

  /*
   * Position after render rather than before it: clamping needs the tooltip's
   * real width, which depends on the date text. Guessing a half-width (the
   * previous approach) let long labels run off the edge of a phone screen.
   */
  useLayoutEffect(() => {
    const el = tipRef.current;
    if (!el || !tip) return;

    const margin = 8;
    const half = el.offsetWidth / 2;
    const left = Math.min(
      Math.max(tip.cx, half + margin),
      window.innerWidth - half - margin
    );

    // Flip below the square when there is no room above — which is the normal
    // case for the top row once the page is scrolled down.
    const below = tip.top - el.offsetHeight - 10 < 0;

    el.style.left = `${left}px`;
    el.style.top = `${below ? tip.bottom : tip.top}px`;
    el.classList.toggle('is-below', below);
    // Keep the arrow on the square even when the body has been clamped.
    el.style.setProperty('--arrow-x', `${tip.cx - left}px`);
  }, [tip]);

  // Touch has no hover, so a tap opens the tooltip; anything else closes it.
  useEffect(() => {
    if (!tip) return;
    const dismiss = (e: Event) => {
      if (e.target instanceof Element && e.target.closest('.heatmap-cell')) return;
      hideTip();
    };
    document.addEventListener('pointerdown', dismiss);
    window.addEventListener('scroll', hideTip, true);
    window.addEventListener('resize', hideTip);
    return () => {
      document.removeEventListener('pointerdown', dismiss);
      window.removeEventListener('scroll', hideTip, true);
      window.removeEventListener('resize', hideTip);
    };
  }, [tip, hideTip]);

  if (error) return <div className="heatmap-error">{error}</div>;
  if (!data) return <div className="heatmap-loading">Loading activity…</div>;

  const activeDays = data.activity.filter((d) => d.count > 0).length;
  const months = Math.round(days / 30);

  return (
    <section className="heatmap">
      <header className="heatmap-header">
        <h2>{title}</h2>
        <span className="heatmap-summary">
          {data.total} post{data.total === 1 ? '' : 's'} in the last{' '}
          {days === 365 ? 'year' : `${months} months`}
        </span>
      </header>

      <div className="heatmap-scroll" ref={scrollRef}>
        <div className="heatmap-grid-wrap">
          <div className="heatmap-months" aria-hidden="true">
            {monthLabels.map(({ index, label }) => (
              <span key={`${label}-${index}`} style={{ gridColumnStart: index + 1 }}>
                {label}
              </span>
            ))}
          </div>

          <div className="heatmap-body">
            <div className="heatmap-weekdays" aria-hidden="true">
              {WEEKDAY_LABELS.map((label, i) => (
                <span key={i}>{label}</span>
              ))}
            </div>

            <div
              className="heatmap-grid"
              // `group`, not `img`: an image role makes its children
              // presentational, which would hide the per-day labels that are
              // the only way a screen reader can read this at all.
              role="group"
              aria-label={`${data.total} posts published on ${activeDays} days in the last ${days} days`}
            >
              {weeks.map((week, wi) => (
                <div className="heatmap-week" key={wi}>
                  {week.map((cell, di) => {
                    const label = describe(cell);
                    return (
                      <span
                        key={`${wi}-${di}`}
                        className={`heatmap-cell level-${cell.inFuture ? 'none' : level(cell.count)}`}
                        // A custom tooltip rather than `title`: the native one
                        // waits about a second before appearing, which on an
                        // 11px square reads as "hovering does nothing".
                        onMouseEnter={(e) => showTip(e.currentTarget, cell)}
                        onMouseLeave={hideTip}
                        onClick={(e) => showTip(e.currentTarget, cell)}
                        onFocus={(e) => showTip(e.currentTarget, cell)}
                        onBlur={hideTip}
                        tabIndex={label ? 0 : -1}
                        aria-label={label ? flatten(label) : undefined}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <footer className="heatmap-legend">
        <span className="heatmap-active-days">
          {activeDays} active day{activeDays === 1 ? '' : 's'}
        </span>
        <span className="heatmap-scale">
          <span>Less</span>
          {[0, 1, 2, 3, 4].map((l) => (
            <span key={l} className={`heatmap-cell level-${l}`} />
          ))}
          <span>More</span>
        </span>
      </footer>

      {/*
        Portalled to <body> deliberately. `.home-container .heatmap` carries a
        `backdrop-filter`, and an element with that property becomes the
        containing block for its own `position: fixed` descendants — so a
        tooltip rendered inside the card had its viewport coordinates measured
        from the card's corner and landed off-screen. It worked on the admin
        page, which has no blur, and silently failed on the home page.
      */}
      {tip &&
        createPortal(
          <div className="heatmap-tip" role="tooltip" ref={tipRef}>
            <span className="heatmap-tip-headline">{tip.headline}</span>
            {tip.authors && <span className="heatmap-tip-authors">{tip.authors}</span>}
          </div>,
          document.body
        )}
    </section>
  );
};

export default ActivityHeatmap;
